import { BoundedConvergence } from "../utils/BoundedConvergence.js";
import Utils from "../utils.js";
import RenderManager from "../rendering/render-manager.js";
import Network from "../miniviews/network.js";
import SlideManager from "../utils/slide-manager.js";

const EPS = 0.000001;
const NEAR = 0.001;
const MIDPOINT_DISTANCE = 1e-1;
const RAYCAST_TARGET_DISTANCE = 1e-5;
const raycaster = new THREE.Raycaster();
const tmpBox = new THREE.Box3();

const ROTATION_DAMPING = 0.4;

class FPSControls {
    constructor(camera, canvas, scene) {
        this._camera = camera;
        this._canvas = canvas;
        this._events = new THREE.EventDispatcher();

        this.invertHorizontal = true;
        this.invertVertical = true;

        this._pointer = { x: null, y: null, prevX: null, prevY: null, isDown: false };
        this._mouseSensitivity = 1;
        this._isKeyDown = {
            up: false,
            down: false,
            left: false,
            right: false
        };
        this._wheelDelta = 0;

        this._helper = new THREE.Mesh(new THREE.BoxBufferGeometry(1, 1, 1), new THREE.MeshBasicMaterial({ color: 0x0000ff, transparent: true, opacity: 0.9, depthTest: false, depthWrite: false }));
        if (Utils.queryParams.has("physcam_helper"))
            scene.add(this._helper);
        this._helper.scale.set(0.2, 0.2, 0.2);

        this._autoRotate = false;
        this._holdAutoRotateTill = 0;
        this._autoRotationDelay = 3000;
        this._autoRotateSpeed = 0;

        this._lastState = null;
    }

    get isFPSControls() { return true; }
    get events() { return this._events; }

    cancelDamping() { this._wheelDelta = 0; }

    isDamping() { return true; }

    setSensitivity(value) { this._mouseSensitivity = value; };

    get isFPSControls() { return true; }
    get object() { return this._camera; }

    onKeyDown(event) { this.onKeyChange(event, true); };
    onKeyUp(event) { this.onKeyChange(event, false); };

    get enabled() { return this._enabled; }
    set enabled(v) { this._enabled = v; }

    onKeyChange(event, isKeyDown) {
        if (["INPUT", "TEXTAREA"].indexOf(event.target.tagName) > -1) {
            return
        }
        const key = event.key.toLowerCase();

        if (key === "w" || key === "arrowup") { this._isKeyDown.up = isKeyDown; }
        else if (key === "a" || key === "arrowleft") { this._isKeyDown.left = isKeyDown; }
        else if (key === "s" || key === "arrowdown") { this._isKeyDown.down = isKeyDown; }
        else if (key === "d" || key === "arrowright") { this._isKeyDown.right = isKeyDown; }
    }

    activate() {
        this._enabled = true;

        const boundingBox = RenderManager.getSceneBoundingBox(tmpBox);

        let speed = 1;
        if (boundingBox) {
            const maxDifference = Math.max(boundingBox.max.x - boundingBox.min.x, boundingBox.max.y - boundingBox.min.y, boundingBox.max.z - boundingBox.min.z);

            speed = (
                isFinite(maxDifference) && maxDifference > 1e-5
                    ? maxDifference
                    : 1e-5
            ) / 50;
        }

        this._forwardOffsetSpeed = new BoundedConvergence(0, 0, -speed, speed, 0.2);
        this._leftOffsetSpeed = new BoundedConvergence(0, 0, -speed, speed, 0.2);

        this._forward = new THREE.Vector3();
        this._camera.getWorldDirection(this._forward);

        this._forward.normalize();
        this._up = new THREE.Vector3(0, 1, 0);
        this._spherical = new THREE.Spherical();

        this._target = new THREE.Vector3().copy(this._forward);

        this._spherical.setFromVector3(this._forward);
        this._spherical.makeSafe();

        this._polarAngle = new BoundedConvergence(this._spherical.phi, this._spherical.phi, 0, Math.PI, ROTATION_DAMPING);
        this._azimuthAngle = new BoundedConvergence(this._spherical.theta, this._spherical.theta, -Infinity, Infinity, ROTATION_DAMPING);

        (() => {

            const mousedown = this.onMouseDown.bind(this);
            const mousemove = this.onMouseMove.bind(this);
            const mouseup = this.onPointerUp.bind(this);
            const mouseleave = this.onPointerLeave.bind(this);
            const keydown = this.onKeyDown.bind(this);
            const keyup = this.onKeyUp.bind(this);
            const wheel = this.onMouseWheel.bind(this);

            this._canvas.addEventListener("touchstart", mousedown);
            this._canvas.addEventListener("touchmove", mousemove);
            this._canvas.addEventListener("touchend", mouseup);

            this._canvas.addEventListener("mousedown", mousedown);
            this._canvas.addEventListener("mousemove", mousemove);
            this._canvas.addEventListener("mouseup", mouseup);
            this._canvas.addEventListener("mouseleave", mouseleave);
            this._canvas.addEventListener("wheel", wheel);
            window.addEventListener("keydown", keydown);
            window.addEventListener("keyup", keyup);

            this.deactivate = () => {
                this._enabled = false;

                this._canvas.classList.remove("rotating");
                this._canvas.classList.remove("rotatable");
                this._canvas.removeEventListener("mousedown", mousedown);
                this._canvas.removeEventListener("mousemove", mousemove);
                this._canvas.removeEventListener("mouseup", mouseup);
                this._canvas.removeEventListener("mouseleave", mouseleave);
                this._canvas.removeEventListener("wheel", wheel);
                window.removeEventListener("keydown", keydown);
                window.removeEventListener("keyup", keyup);

                this.deactivate = () => { };
            }
        })();

        this._helper.position.copy(this._forward).setLength(250).add(this._camera.position);
    }

    deactivate() { }

    onMouseWheel(event) {
        this._wheelDelta += event.deltaY;
    }

    onMouseDown(event) {
        const x = (!!event.touches ? event.touches[0] : event).clientX;
        const y = (!!event.touches ? event.touches[0] : event).clientY;

        this._pointer.x = x;
        this._pointer.y = y;
        this._pointer.isDown = true;

        this._canvas.classList.add("rotating");
        this._canvas.classList.remove("rotatable");
    }

    onMouseMove(event) {
        event.preventDefault();

        if (this._pointer.isDown) {
            const x = (!!event.touches ? event.touches[0] : event).clientX;
            const y = (!!event.touches ? event.touches[0] : event).clientY;

            this._canvas.classList.add("rotating");
            this._canvas.classList.remove("rotatable");
            this.rotate(x * (this.invertHorizontal ? -1 : 1), y * (this.invertVertical ? -1 : 1));
        }
        else {
            this._canvas.classList.remove("rotating");
            this._canvas.classList.add("rotatable");
        }
    }

    rotate(x, y) {
        this._pointer.x = x;
        this._pointer.y = y;

        if (this._pointer.prevX !== null && this._pointer.prevY !== null) {
            const deltaX = THREE.Math.degToRad(this._pointer.x - this._pointer.prevX) * this._mouseSensitivity / 10;
            const deltaY = THREE.Math.degToRad(this._pointer.y - this._pointer.prevY) * this._mouseSensitivity / 10;

            this._polarAngle.increaseEndBy(deltaY);
            this._azimuthAngle.decreaseEndBy(deltaX);
        }

        this._pointer.prevX = x;
        this._pointer.prevY = y;
    }

    setAzimuthalAngle(theta) { this._azimuthAngle.reset(theta, theta); }

    setPolarAngle(phi) { this._polarAngle.reset(phi, phi); }

    get lastSetByUserPosition() { return this._lastState; }
    set lastSetByUserPosition(v) { this._lastState = v; }

    onPointerUp() {
        this._endUserAction();
        this._delayAutoRotation();
    }

    onPointerLeave() {
        this._endUserAction();
    }

    _endUserAction() {
        this._pointer.isDown = false;
        this._pointer.x = null;
        this._pointer.y = null;
        this._pointer.prevX = null;
        this._pointer.prevY = null;
        this._canvas.classList.remove("rotating");
        this._canvas.classList.remove("rotatable");
    }

    get target() { return this._target; }

    _isForwardVector(normal) {
        if (normal.length() === 0) return true;

        var forward = this._camera.getWorldDirection(new THREE.Vector3());

        return forward.distanceToSquared(normal) < EPS;
    }

    _pickTarget(pos, dir) {
        const tmp = new THREE.Vector3();

        let itsct = RenderManager.raycaster.pick(tmp, this._camera);

        let depth;
        if (itsct.objectId === 0) {
            raycaster.setFromCamera(tmp, this._camera);
            itsct = raycaster.intersectObjects(Network.gates)[0] || null;
            if (itsct) {
                depth = itsct.distance;
                itsct.object.getWorldPosition(itsct.point);
            }
        } else depth = itsct.depth;

        if (itsct) {
            const out = new THREE.Vector3();

            if (Math.log2(depth) * 0.5 < MIDPOINT_DISTANCE) {
                const midpoint = itsct.point.add(this._camera.position).divideScalar(2);

                out.copy(midpoint);
            } else {
                const point = itsct.point.sub(dir.multiplyScalar(RAYCAST_TARGET_DISTANCE));

                out.copy(point);
            }

            return out;
        }

        return null;
    }

    update() {
        if (!this._enabled || (Q3.selected && !Q3.selected.isObject3D)) return;

        if (this._autoRotate && !this._pointer.isDown && this._holdAutoRotateTill < Date.now()) {
            let angle = 2 * Math.PI / 60 / 60 * this._autoRotateSpeed;
            this._azimuthAngle.increaseEndBy(angle / ROTATION_DAMPING);
        }

        const zoomSpeed = SlideManager.currentSlide.controls.zoomSpeed || Q3.data.setups.controls.zoomSpeed || 1;

        let forwardValue = 0;
        let leftValue = 0;

        if (this._isKeyDown.up) { forwardValue += 1; }
        if (this._isKeyDown.down) { forwardValue -= 1; }
        if (this._isKeyDown.left) { leftValue += 1; }
        if (this._isKeyDown.right) { leftValue -= 1; }

        if (Math.abs(this._wheelDelta) > 5) forwardValue = -THREE.Math.clamp(this._wheelDelta, -1, 1);

        const len = Math.sqrt(forwardValue * forwardValue + leftValue * leftValue);
        if (len > 0) {
            forwardValue /= len;
            leftValue /= len;
        }

        this._forwardOffsetSpeed.end = forwardValue * 0.8;
        this._leftOffsetSpeed.end = leftValue;

        let oldAzimuthAngle = this._azimuthAngle.start;
        let oldPolarAngle = this._polarAngle.start;

        this._azimuthAngle.update();
        this._polarAngle.update();
        this._forwardOffsetSpeed.update();
        this._leftOffsetSpeed.update();

        let newAzimuthAngle = this._azimuthAngle.start;
        let newPolarAngle = this._polarAngle.start;
        let azimuthSpeed = newAzimuthAngle - oldAzimuthAngle;
        let polarSpeed = newPolarAngle - oldPolarAngle;
        let forwardSpeed = this._forwardOffsetSpeed.start;
        let leftSpeed = this._leftOffsetSpeed.start;

        this._camera.getWorldDirection(this._target);
        this._target.normalize();

        const leftVector = this._up.clone().cross(this._target).normalize().multiplyScalar(this._leftOffsetSpeed.start);

        const forward = new THREE.Vector3().add(this._target.clone().multiplyScalar(this._forwardOffsetSpeed.start)).add(leftVector);
        const newPosition = new THREE.Vector3().copy(this._camera.position).add(forward);

        const normal = new THREE.Vector3().subVectors(newPosition, this._camera.position).normalize();

        const cameraLen = this._camera.getWorldPosition(new THREE.Vector3()).length() * 0.02;
        let dist = cameraLen;

        if (true || this._isForwardVector(normal)) {
            const newTarget = this._pickTarget(this._camera.position, normal);

            if (newTarget) {
                dist = THREE.Math.clamp(newTarget.distanceToSquared(this._camera.position) * 0.04, 0, cameraLen);

                this._helper.position.copy(newTarget);
            }
        }

        forward.multiplyScalar(dist * zoomSpeed);
        newPosition.copy(this._camera.position).add(forward);

        this._camera.position.copy(newPosition);

        this._spherical.theta = this._azimuthAngle.start;
        this._spherical.phi = this._polarAngle.start;
        this._spherical.makeSafe();

        this._forward.setFromSpherical(this._spherical);
        this._target.copy(this._camera.position).add(this._forward);
        this._camera.lookAt(this._target);

        this._wheelDelta = Math.sign(this._wheelDelta) * (Math.pow(Math.abs(this._wheelDelta), 0.85));
        this._events.dispatchEvent({
            type: 'change',
            azimuthSpeed, polarSpeed, forwardSpeed, leftSpeed,
        });

        this._lastState = {
            target: this._target.clone(),
            position: this._camera.position.clone()
        };
    }

    setPanLimits() { }

    get autoRotate() {
        return this._autoRotate;
    }

    set autoRotate(v) {
        v = Boolean(v);
        if (v === this._autoRotate) return;

        this._autoRotate = v;
        this._delayAutoRotation();
    }

    get autoRotateSpeed() {
        return this._autoRotateSpeed;
    }

    set autoRotateSpeed(s) {
        this._autoRotateSpeed = s;
    }

    setAutoRotationDelay(delay) {
        this._autoRotationDelay = 1000 * delay;
        this._delayAutoRotation();
    }

    _delayAutoRotation() {
        this._holdAutoRotateTill = Date.now() + this._autoRotationDelay;
    }
}

export default FPSControls;
export { FPSControls };